import React, { useState, useEffect } from 'react';
import { Building2, MapPin, Phone, Mail, Globe, Shield, Database, Clock, Users, Pencil, Save } from 'lucide-react';
import { api } from '../../services/api';

const CompanyConfiguration = ({ onChange }) => {
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Get companyId from user context or localStorage
  const [companyId] = useState(() => {
    // Try multiple sources for company ID
    const storedCompanyId = localStorage.getItem('companyId');
    const storedCompanyData = localStorage.getItem('company');
    const storedUserData = localStorage.getItem('user');
    
    if (storedCompanyId && storedCompanyId !== 'demo-company-id') {
      return storedCompanyId;
    }
    
    if (storedCompanyData) {
      try {
        const companyData = JSON.parse(storedCompanyData);
        if (companyData.id && companyData.id !== 'demo-company-id') {
          return companyData.id;
        }
      } catch (e) {
        console.warn('Failed to parse company data from localStorage');
      }
    }
    
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        if (userData.companyId && userData.companyId !== 'demo-company-id') {
          return userData.companyId;
        }
      } catch (e) {
        console.warn('Failed to parse user data from localStorage');
      }
    }
    
    console.warn('No valid company ID found, this may cause API calls to fail');
    return null;
  });

  // Fetch company settings on component mount
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.companySettings.get(companyId);
        setCompanySettings(response.settings);
      } catch (err) {
        console.error('Failed to fetch company settings:', err);
        setError('Failed to load company settings');
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchCompanySettings();
    } else {
      setLoading(false);
      setError('No company ID available. Please ensure you are properly logged in.');
    }
  }, [companyId]);

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (UTC-5)' },
    { value: 'America/Chicago', label: 'Central Time (UTC-6)' },
    { value: 'America/Denver', label: 'Mountain Time (UTC-7)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)' }
  ];

  const safetyRatings = [
    'Satisfactory',
    'Conditional',
    'Unsatisfactory',
    'Not Rated'
  ];

  const operatingRegions = [
    'Northeast', 'Southeast', 'Southwest', 'Northwest',
    'Midwest', 'West Coast', 'East Coast', 'Central'
  ];

  const serviceTypes = [
    'Long Haul', 'Regional', 'Local Delivery', 'Expedited',
    'Refrigerated', 'Flatbed', 'Tanker', 'Specialized Transport'
  ];

  const handleCompanyChange = (field, value) => {
    if (!companySettings || !isEditing) return;

    let updatedCompanySettings;
    if (field.includes('.')) {
      const [section, subField] = field.split('.');
      updatedCompanySettings = {
        ...companySettings,
        company: {
          ...companySettings.company,
          [section]: {
            ...companySettings.company[section],
            [subField]: value
          }
        }
      };
    } else {
      updatedCompanySettings = {
        ...companySettings,
        company: {
          ...companySettings.company,
          [field]: value
        }
      };
    }

    setCompanySettings(updatedCompanySettings);
  };

  const handleOperationalChange = (field, value) => {
    if (!companySettings || !isEditing) return;

    let updatedCompanySettings;
    if (field.includes('.')) {
      const [section, subField] = field.split('.');
      updatedCompanySettings = {
        ...companySettings,
        operational: {
          ...companySettings.operational,
          [section]: {
            ...companySettings.operational[section],
            [subField]: value
          }
        }
      };
    } else {
      updatedCompanySettings = {
        ...companySettings,
        operational: {
          ...companySettings.operational,
          [field]: value
        }
      };
    }

    setCompanySettings(updatedCompanySettings);
  };

  const handleComplianceChange = (field, value) => {
    if (!companySettings || !isEditing) return;

    const updatedCompanySettings = {
      ...companySettings,
      compliance: {
        ...companySettings.compliance,
        [field]: value
      }
    };

    setCompanySettings(updatedCompanySettings);
  };

  const handleSystemChange = (field, value) => {
    if (!companySettings || !isEditing) return;

    let updatedCompanySettings;
    if (field.includes('.')) {
      const [section, subField] = field.split('.');
      updatedCompanySettings = {
        ...companySettings,
        system: {
          ...companySettings.system,
          [section]: {
            ...companySettings.system[section],
            [subField]: value
          }
        }
      };
    } else {
      updatedCompanySettings = {
        ...companySettings,
        system: {
          ...companySettings.system,
          [field]: value
        }
      };
    }

    setCompanySettings(updatedCompanySettings);
  };

  const handleSave = async () => {
    if (!companySettings || !companyId) {
      setError('Cannot save company settings without a valid company ID.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      await api.companySettings.update(companyId, companySettings);
      setIsEditing(false);
      onChange();
    } catch (err) {
      console.error('Failed to save company settings:', err);
      setError('Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!companyId) {
      setError('Cannot reload company settings without a valid company ID.');
      return;
    }
    try {
      setLoading(true);
      const response = await api.companySettings.get(companyId);
      setCompanySettings(response.settings);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to reload company settings:', err);
      setError('Failed to reload company settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading company settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!companySettings || loading) {
    return (
      <div className="text-center py-8">
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading company settings...</p>
          </>
        ) : (
          <p className="text-gray-500">No company settings found</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Company Settings</h2>
          <p className="text-sm text-gray-600">Review and update your company profile and operational preferences.</p>
        </div>
        <div className="flex items-center gap-3">
          {!isEditing ? (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={!companySettings}
              className={`inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md shadow-sm transition-colors ${
                companySettings
                  ? 'bg-white text-blue-600 hover:bg-blue-50'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
              }`}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 border rounded-md text-sm font-medium transition-colors ${
                  saving
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors ${
                  saving ? 'bg-blue-300 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
      {saving && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-blue-700 text-sm">Saving changes...</span>
          </div>
        </div>
      )}
      {/* Company Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Building2 className="h-5 w-5 mr-2 text-blue-600" />
          Company Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={companySettings?.company?.name || ''}
              onChange={(e) => handleCompanyChange('name', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Legal Name</label>
            <input
              type="text"
              value={companySettings?.company?.legalName || ''}
              onChange={(e) => handleCompanyChange('legalName', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">DOT Number</label>
            <input
              type="text"
              value={companySettings?.company?.dotNumber || ''}
              onChange={(e) => handleCompanyChange('dotNumber', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">MC Number</label>
            <input
              type="text"
              value={companySettings?.company?.mcNumber || ''}
              onChange={(e) => handleCompanyChange('mcNumber', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID</label>
            <input
              type="text"
              value={companySettings?.company?.taxId || ''}
              onChange={(e) => handleCompanyChange('taxId', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin className="h-5 w-5 mr-2 text-blue-600" />
          Business Address
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
            <input
              type="text"
              value={companySettings?.company?.address?.street || ''}
              onChange={(e) => handleCompanyChange('address.street', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <input
              type="text"
              value={companySettings?.company?.address?.city || ''}
              onChange={(e) => handleCompanyChange('address.city', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
            <select
              value={companySettings?.company?.address?.state || ''}
              onChange={(e) => handleCompanyChange('address.state', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            >
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
            <input
              type="text"
              value={companySettings?.company?.address?.zipCode || ''}
              onChange={(e) => handleCompanyChange('address.zipCode', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
            <input
              type="text"
              value={companySettings?.company?.address?.country || ''}
              onChange={(e) => handleCompanyChange('address.country', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="h-5 w-5 mr-2 text-blue-600" />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="inline h-4 w-4 mr-1" />
              Phone
            </label>
            <input
              type="tel"
              value={companySettings?.company?.contact?.phone || ''}
              onChange={(e) => handleCompanyChange('contact.phone', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-4 w-4 mr-1" />
              Email
            </label>
            <input
              type="email"
              value={companySettings?.company?.contact?.email || ''}
              onChange={(e) => handleCompanyChange('contact.email', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="inline h-4 w-4 mr-1" />
              Website
            </label>
            <input
              type="url"
              value={companySettings?.company?.contact?.website || ''}
              onChange={(e) => handleCompanyChange('contact.website', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact</label>
            <input
              type="tel"
              value={companySettings?.company?.contact?.emergencyContact || ''}
              onChange={(e) => handleCompanyChange('contact.emergencyContact', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
        </div>
      </div>

      {/* Operational Settings */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-blue-600" />
          Operational Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              value={companySettings?.operational?.timezone || ''}
              onChange={(e) => handleOperationalChange('timezone', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fleet Size</label>
            <input
              type="number"
              min="1"
              value={companySettings?.operational?.fleetSize || ''}
              onChange={(e) => handleOperationalChange('fleetSize', parseInt(e.target.value))}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Driver Count</label>
            <input
              type="number"
              min="1"
              value={companySettings?.operational?.driverCount || ''}
              onChange={(e) => handleOperationalChange('driverCount', parseInt(e.target.value))}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours Start</label>
            <input
              type="time"
              value={companySettings?.operational?.businessHours?.start || ''}
              onChange={(e) => handleOperationalChange('businessHours.start', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours End</label>
            <input
              type="time"
              value={companySettings?.operational?.businessHours?.end || ''}
              onChange={(e) => handleOperationalChange('businessHours.end', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
        </div>
      </div>

      {/* Compliance Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2 text-blue-600" />
          Compliance Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Provider</label>
            <input
              type="text"
              value={companySettings?.compliance?.insuranceProvider || ''}
              onChange={(e) => handleComplianceChange('insuranceProvider', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Policy Number</label>
            <input
              type="text"
              value={companySettings?.compliance?.insurancePolicyNumber || ''}
              onChange={(e) => handleComplianceChange('insurancePolicyNumber', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Safety Rating</label>
            <select
              value={companySettings?.compliance?.safetyRating || ''}
              onChange={(e) => handleComplianceChange('safetyRating', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            >
              {safetyRatings.map(rating => (
                <option key={rating} value={rating}>{rating}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Officer</label>
            <input
              type="text"
              value={companySettings?.compliance?.complianceOfficer || ''}
              onChange={(e) => handleComplianceChange('complianceOfficer', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Audit Date</label>
            <input
              type="date"
              value={companySettings?.compliance?.lastAuditDate || ''}
              onChange={(e) => handleComplianceChange('lastAuditDate', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Next Audit Date</label>
            <input
              type="date"
              value={companySettings?.compliance?.nextAuditDate || ''}
              onChange={(e) => handleComplianceChange('nextAuditDate', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
        </div>
      </div>

      {/* System Settings */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2 text-blue-600" />
          System Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Retention (months)</label>
            <input
              type="number"
              min="1"
              max="120"
              value={companySettings?.system?.dataRetention || ''}
              onChange={(e) => handleSystemChange('dataRetention', parseInt(e.target.value))}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
            <input
              type="number"
              min="5"
              max="480"
              value={companySettings?.system?.sessionTimeout || ''}
              onChange={(e) => handleSystemChange('sessionTimeout', parseInt(e.target.value))}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
            <select
              value={companySettings?.system?.backupFrequency || ''}
              onChange={(e) => handleSystemChange('backupFrequency', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password Expiration (days)</label>
            <input
              type="number"
              min="30"
              max="365"
              value={companySettings?.system?.passwordPolicy?.expirationDays || ''}
              onChange={(e) => handleSystemChange('passwordPolicy.expirationDays', parseInt(e.target.value))}
              disabled={!isEditing}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                !isEditing ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyConfiguration;
